import React, { useEffect, useRef, useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../ui/Button';
interface HeaderProps {
  setPage: (page: string) => void;
  currentPage: string;
}
// Highest-priority services surfaced directly in the nav.
// Lower-priority services live behind "Additional Services".
const priorityServices = [{
  name: 'Automotive Sandblasting',
  id: 'services'
}, {
  name: 'Truck Frame Blasting',
  id: 'services'
}, {
  name: 'Mobile Sandblasting',
  id: 'services'
}, {
  name: 'Boat Bottom Paint Removal',
  id: 'services'
}, {
  name: 'Concrete & Brick Prep',
  id: 'services'
}, {
  name: 'Heavy Equipment Blasting',
  id: 'services'
}, {
  name: 'Graffiti Removal',
  id: 'services'
}, {
  name: 'Media Blasting',
  id: 'services'
}];
export function Header({
  setPage,
  currentPage
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close the desktop dropdown on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setServicesOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  const navLinks = [{
    name: 'Home',
    id: 'home'
  }, {
    name: 'Industries',
    id: 'industries'
  }, {
    name: 'About',
    id: 'about'
  }, {
    name: 'Contact',
    id: 'contact'
  }];
  const handleNavClick = (id: string) => {
    setPage(id);
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setServicesOpen(false);
    window.scrollTo(0, 0);
  };
  const servicesActive = currentPage === 'services' || currentPage === 'services-hub';
  return <header className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 backdrop-blur-sm py-4 border-b border-steel-100'}`} data-id="element-77" data-mp="30g1vkd5jmrxk">
      <div className="max-w-7xl mx-auto px-4 flex justify-between items-center" data-id="element-78" data-mp="27vtmxs2ngu84">
        {/* Logo */}
        <button onClick={() => handleNavClick('home')} className="focus:outline-none group" aria-label="Merem Blasting Home" data-id="element-79" data-mp="22wz2klp1axlw">
          <img src="https://drwzpk38qkpfb.cloudfront.net/www.meremblasting.com/uploaded/images/Misc%20icons%20and%20images/logo.png" alt="Merem Blasting" className="h-12 md:h-16 w-auto transition-opacity group-hover:opacity-80" data-id="element-80" data-mp="n928kdrlyigf" />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-8" data-id="element-81" data-mp="122nk1b4e2ybw">
          <ul className="flex items-center space-x-6" data-id="element-82" data-mp="165bs3gla417t">
            <li data-id="element-83" data-mp="s6kj28l9x5qj">
              <button onClick={() => handleNavClick('home')} className={`font-heading uppercase font-semibold tracking-wider text-base transition-colors hover:text-brand-red ${currentPage === 'home' ? 'text-brand-red' : 'text-charcoal'}`} data-id="element-84" data-mp="s73sf0wuylsy">
                Home
              </button>
            </li>

            {/* Services dropdown */}
            <li ref={servicesRef} className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-id="element-85" data-mp="zwt3mlo129v">
              <button onClick={() => setServicesOpen(prev => !prev)} aria-expanded={servicesOpen} aria-haspopup="true" className={`flex items-center font-heading uppercase font-semibold tracking-wider text-base transition-colors hover:text-brand-red ${servicesActive ? 'text-brand-red' : 'text-charcoal'}`} data-id="element-86" data-mp="3ocpqkfwqniy2">
                Services
                <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-87" data-mp="ax9uvm30qh5k" />
              </button>

              <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[520px] transition-all duration-200 origin-top ${servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'}`} data-id="element-88" data-mp="2votvtgqqc13x">
                <div className="bg-white border border-steel-200 shadow-2xl" data-id="element-89" data-mp="2lkmyjpvytovs">
                  <div className="border-t-4 border-brand-red" data-id="element-90" data-mp="2liiqqwuy45q8" />
                  <div className="p-5" data-id="element-91" data-mp="2qiy6tw6m3rbw">
                    <p className="font-heading font-bold uppercase tracking-widest text-xs text-steel-500 mb-4" data-id="element-92" data-mp="2qhverkxjziue">
                      Blasting &amp; Surface Prep
                    </p>
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1" data-id="element-93" data-mp="2ze4e6zlj6hgj">
                      {priorityServices.map(svc => <li key={svc.name} data-id="element-94" data-mp="39ah920stb1cv">
                          <button onClick={() => handleNavClick(svc.id)} className="w-full text-left font-heading font-semibold text-sm uppercase tracking-wide text-charcoal hover:text-brand-red hover:bg-steel-50 px-3 py-2.5 transition-colors" data-id="element-95" data-mp="39aqz1odux8ik">
                            {svc.name}
                          </button>
                        </li>)}
                    </ul>
                  </div>
                  <div className="border-t border-steel-200 bg-steel-50 px-5 py-4 flex items-center justify-between" data-id="element-96" data-mp="16x1tt66e2u80">
                    <button onClick={() => handleNavClick('services-hub')} className="font-heading font-bold text-sm uppercase tracking-wider text-charcoal hover:text-brand-red transition-colors" data-id="element-97" data-mp="1lqojy3nqqk03">
                      Additional Services →
                    </button>
                    <button onClick={() => handleNavClick('contact')} className="font-heading font-bold text-sm uppercase tracking-wider text-brand-red hover:text-brand-red-dark transition-colors" data-id="element-98" data-mp="24izg7enw3jp8">
                      Send Photos For A Quote
                    </button>
                  </div>
                </div>
              </div>
            </li>

            {navLinks.slice(1).map(link => <li key={link.id} data-id="element-99" data-mp="2s4jkqp9g90xc">
                <button onClick={() => handleNavClick(link.id)} className={`font-heading uppercase font-semibold tracking-wider text-base transition-colors hover:text-brand-red ${currentPage === link.id ? 'text-brand-red' : 'text-charcoal'}`} data-id="element-100" data-mp="2x6902h3pg0r8">
                  {link.name}
                </button>
              </li>)}
          </ul>
          <Button variant="primary" size="sm" onClick={() => handleNavClick('contact')} data-id="element-101" data-mp="2e7ekx70bwyaj">
            Get a Quote
          </Button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button className="lg:hidden p-2 text-charcoal hover:text-brand-red focus:outline-none" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-102" data-mp="24i3bfj9j5rm4">
          {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-103" data-mp="2ayws17g798tq" /> : <MenuIcon className="w-6 h-6" data-id="element-104" data-mp="2awakh7tb6tbi" />}
        </button>
      </div>

      {/* Mobile Nav Dropdown */}
      <div className={`lg:hidden absolute top-full left-0 w-full bg-white shadow-xl border-t border-steel-100 transition-all duration-300 origin-top max-h-[80vh] overflow-y-auto ${mobileMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}`} data-id="element-105" data-mp="21xovoux8ui2a">
        <nav className="flex flex-col px-4 py-6" data-id="element-106" data-mp="1s1dhfze3fi57">
          <button onClick={() => handleNavClick('home')} className={`text-left font-heading uppercase font-bold tracking-wider text-xl py-3 border-b border-steel-100 ${currentPage === 'home' ? 'text-brand-red' : 'text-charcoal'}`} data-id="element-107" data-mp="1n7rdqwpwqolj">
            Home
          </button>

          {/* Mobile services accordion */}
          <div className="border-b border-steel-100" data-id="element-108" data-mp="19avru3ok52o2">
            <button onClick={() => setMobileServicesOpen(prev => !prev)} aria-expanded={mobileServicesOpen} className={`w-full flex items-center justify-between text-left font-heading uppercase font-bold tracking-wider text-xl py-3 ${servicesActive ? 'text-brand-red' : 'text-charcoal'}`} data-id="element-109" data-mp="14azpe8vidjam">
              Services
              <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-110" data-mp="3txkys5ktu1ef" />
            </button>
            {mobileServicesOpen && <ul className="pb-4 pl-3 border-l-2 border-brand-red ml-1 space-y-1" data-id="element-111" data-mp="326ycourmy7xw">
                {priorityServices.map(svc => <li key={svc.name} data-id="element-112" data-mp="36672xpngvnop">
                    <button onClick={() => handleNavClick(svc.id)} className="w-full text-left font-heading font-semibold uppercase tracking-wide text-sm text-charcoal hover:text-brand-red py-2.5" data-id="element-113" data-mp="3axyplp3i599k">
                      {svc.name}
                    </button>
                  </li>)}
                <li data-id="element-114" data-mp="20yu66jodao40">
                  <button onClick={() => handleNavClick('services-hub')} className="w-full text-left font-heading font-bold uppercase tracking-wide text-sm text-brand-red py-2.5" data-id="element-115" data-mp="25vsz4ywaop0m">
                    Additional Services →
                  </button>
                </li>
              </ul>}
          </div>

          {navLinks.slice(1).map(link => <button key={link.id} onClick={() => handleNavClick(link.id)} className={`text-left font-heading uppercase font-bold tracking-wider text-xl py-3 border-b border-steel-100 ${currentPage === link.id ? 'text-brand-red' : 'text-charcoal'}`} data-id="element-116" data-mp="1m72439dl36k8">
              {link.name}
            </button>)}

          <div className="pt-5" data-id="element-117" data-mp="ygerwbwmxurv">
            <Button variant="primary" className="w-full" onClick={() => handleNavClick('contact')} data-id="element-118" data-mp="13gkehdo1sr33">
              Get a Quote
            </Button>
          </div>
        </nav>
      </div>
    </header>;
}